/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Button, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Alert from 'react-s-alert';
import { Link } from 'react-router-dom';
import Joyride, { STATUS } from 'react-joyride';
import { MultiSelect } from 'react-multi-select-component';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import ProviderService from '../../services/ProviderService';
import GeneralService from '../../services/GeneralService';

// local imports
import UserNotApproved from './UserNotApproved';
import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';
import ProviderNavbar from './ProviderNavbar';
import Clients from './Clients';

const MyClients = props => {

    const [clientList, setClientList] = useState([]);
    const [modal, setModal] = useState({ visible: false, data: { recipientName: "", inviteType: "email", recipientMobile: "", recipientEmail: "" } });
    const [customEmailModal, setCustomEmailModal] = useState({ visible: false });
    const [customEmailClientList, setCustomEmailClientList] = useState([]);
    const [selectedCustomRecipients, setselectedCustomRecipients] = useState([]);
    const [selectedCustomClients, setSelectedCustomClients] = useState([]);
    const [customEmailSubject, setCustomEmailSubject] = useState('');
    const [customEmailBody, setCustomEmailBody] = useState('');
    const [customEmailMsgPreview, setCustomEmailMsgPreview] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    const [emailRecipients, setEmailRecipients] = useState([{
        name: "",
        email: "",
    }]);
    const [waLink, setWaLink] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [msgPreview, setMsgPreview] = useState("");
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: [`Looks like your profile is incomplete! Please complete that by going to `, <b key="profileLink"><Link to='/counsellor/profile'>Profile</Link></b>, ` page to enjoy our service. Unless your profile is completed you won't be visible to any clients.`],
            msgError: true
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>All your clients and their details will appear here!</h2>,
                placement: 'center',
                target: '#myClientsDiv',
            },
            {
                content: <div className="tooltipDiv">Send custom emails to your existing clients by pressing this button.</div>,
                placement: 'right',
                target: '#customEmailBtn',
            },
            {
                content: <div className="tooltipDiv">Send invite to new client via Email, SMS and WhatsApp by pressing this button.</div>,
                placement: 'right',
                target: '#inviteBtn',
            }

        ]
    });
    let helpers = null;
    const [notifications, setNotifications] = useState({ newClientAdded: [] });

    // contexts
    const { theme } = useContext(ThemeContext);
    const { user, setUser, userApprovalStatus } = useContext(AuthContext);

    // useEffect
    useEffect(() => {
        ProviderService.getClients()
            .then(data => {
                try {
                    if (!user.guideTakenFor.includes("MyClients")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    setProfileComplete({ ...profileComplete, status: data.profileComplete });
                    setClientList(data.clients);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Provider MyClients useEffect', 'Error in try catch block', err.stack);
                }
            });
        getNotifications();
    }, []);

    // setup label and values for custom email modal
    useEffect(() => {
        let tempArray = [];
        for (let i = 0; i < clientList.length; i++) {
            tempArray[i] = {
                label: clientList[i].clientId.email,
                value: clientList[i].clientId.email
            }

        }
        setCustomEmailClientList(tempArray);
        // console.log(tempArray, clientList);
    }, [clientList]);

    // update selected custom clients with client name when recipients changes
    useEffect(() => {
        let tempArray = [];
        tempArray = clientList.filter((client) => selectedCustomRecipients.map((item) => item.value).includes(client.clientId.email));
        // console.log('tempArray', tempArray);
        let tempArray1 = [];
        for (let i = 0; i < tempArray.length; i++) {
            tempArray1[i] = {
                email: tempArray[i].clientId.email,
                name: tempArray[i].clientId.firstName + " " + tempArray[i].clientId.lastName
            }
        }
        // console.log('tempArray1', tempArray1);
        setSelectedCustomClients(tempArray1);
    }, [selectedCustomRecipients]);

    // set custom email msg preview
    useEffect(() => {
        let tempData =
            `Dear ${selectedCustomClients.map((item) => item.name)},

${customEmailBody}

Yours Sincerely,
${user.username}
(Counsellor at Umang Wellness)`

        setCustomEmailMsgPreview(tempData);
    }, [selectedCustomClients, selectedCustomRecipients, customEmailSubject, customEmailBody]);

    // update copy
    useEffect(() => {
        setIsCopied(false);
    }, [selectedCustomRecipients]);

    useEffect(() => {
        let preview = "";
        if (modal.data.inviteType === "email") {
            preview = `Dear ${emailRecipients ? Array.prototype.map.call(emailRecipients, function (recipient) { return recipient.name; }).join(", ") : '<Recipient name>'},\n\nThis is ${user.username} and it's my pleasure to invite you for a psychological counselling session with me on the Umang Wellness platform.  Please click https://www.umangwellness.com to sign up and book a session with me.\n\nI look forward to hear from you.\n\nThanking you,\nSincerely,\n${user.username}`;
        } else if (modal.data.inviteType === "sms") {
            preview = `Dear ${modal.data.recipientName ? modal.data.recipientName : '<Recipient name>'},\n\nYou are invited to join Umang Wellness at https://www.umangwellness.com to have a psychological counselling session with me.\n\nThank you,\n${user.username}`;
        } else if (modal.data.inviteType === "whatsapp") {
            preview = `Dear ${modal.data.recipientName ? modal.data.recipientName : '<Recipient name>'},\n\nThis is ${user.username} and it's my pleasure to invite you for a psychological counselling session with me on the Umang Wellness platform.  Please click https://www.umangwellness.com to sign up and book a session with me.\n\nI look forward to hear from you.\n\nThanking you,\nSincerely,\n${user.username}`;
            setWaLink(`https://api.WhatsApp.com/send?phone=91${modal.data.recipientMobile}&text=${encodeURI(preview)}`);
        }
        setMsgPreview(preview);
    }, [modal.data, emailRecipients]);

    const getNotifications = () => {
        AuthService.getNotifications().then(data => {
            try {
                let temp = { newClientAdded: [] };
                Promise.all(data.notifications.MyClient.map(item => {
                    if (!item.readStatus && item.title === "newClientAdded") {
                        temp.newClientAdded.push(item.description);
                    }
                    AuthService.markNotificationAsRead("MyClient", item._id);
                })).then(() => {
                    setNotifications(temp);
                });
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Provider MyClients getNotifications', 'Error in try catch block', err.stack);
            }
        })
    }

    const openFormResponses = (index) => {
        try {
            props.history.push({
                pathname: "/counsellor/clientFormResponse",
                state: {
                    id: clientList[index].clientId.user,
                    profileImage: clientList[index].clientId.profileImage,
                    name: `${clientList[index].clientId.firstName} ${clientList[index].clientId.lastName}`
                }
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider MyClients openFormResponses', 'Error in try catch block', err.stack);
        }
    }

    const handleChange = e => {
        try {
            setModal({ ...modal, data: { ...modal.data, [e.target.name]: e.target.value } });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider MyClients handleChange', 'Error in try catch block', err.stack);
        }
    }

    // handle onChange of Custom Email
    const handleOnChangeCustomEmail = val => {
        setselectedCustomRecipients(val);
        // console.log(selectedCustomRecipients);
    }

    // handle onChange customEmailSubject
    const handleCustomEmailSubject = e => {
        setCustomEmailSubject(e.target.value)
    }

    // handle onChange customEmailBody
    const handleCustomEmailBody = e => {
        setCustomEmailBody(e.target.value);
    }

    // handle onChange of Email Recipients
    const handleEmailRecipientsNameChange = id => e => {
        const newEmailRecipients = emailRecipients.map((recipient, rid) => {
            if (id !== rid) return recipient;
            return { ...recipient, name: e.target.value }
        });
        setEmailRecipients(newEmailRecipients);
    }

    const handleEmailRecipientsEmailChange = id => e => {
        const newEmailRecipients = emailRecipients.map((recipient, rid) => {
            if (id !== rid) return recipient;
            return { ...recipient, email: e.target.value }
        });
        setEmailRecipients(newEmailRecipients);
    }

    // handle - add new email recipient
    const handleAddEmailRecipient = () => {
        const newEmailRecipients = emailRecipients.concat({ name: "", email: "" });
        setEmailRecipients(newEmailRecipients);
    }

    // handle - remove an email recipient
    const handleRemoveEmailRecipient = id => () => {
        const newEmailRecipients = emailRecipients.filter((recipient, rid) => id !== rid);
        setEmailRecipients(newEmailRecipients);
    }

    // send Custom Email helper function
    const sendCustomEmail = e => {
        e.preventDefault();
        setIsLoading(true);

        let dataToSend = {
            selectedCustomRecipients: selectedCustomClients,
            customEmailSubject: customEmailSubject,
            customEmailBody: customEmailBody
        }
        ProviderService.sendCustomEmail(dataToSend)
            .then(data => {
                setCustomEmailModal({ visible: false });
                setIsLoading(false);
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setCustomEmailSubject('');
                setCustomEmailBody('');
                setselectedCustomRecipients([]);
                setSelectedCustomClients([]);
                setIsCopied(false);
            })
            .catch(error => {
                setIsLoading(false);
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
            })
    }

    const copyBCCString = () => {
        navigator.clipboard.writeText(selectedCustomRecipients.map((item) => item.value).join(", "));
        setIsCopied(true);
    }

    // sendInvite helper function
    const sendInvite = e => {
        e.preventDefault();
        try {
            setIsLoading(true);
            if (modal.data.inviteType === "whatsapp") {
                document.getElementById("waLink").click();
                setModal({ visible: false, data: { recipientName: "", inviteType: "email", recipientMobile: "", recipientEmail: "" } });
                setIsLoading(false);
            }
            else if (modal.data.inviteType === "email") {
                // Call ProviderService.send multiple invites
                let dataToSend = {
                    emailRecipients: emailRecipients,
                    inviteType: modal.data.inviteType
                }
                ProviderService.sendMultipleInvite(dataToSend)
                    .then(data => {
                        setModal({ visible: false, data: { recipientName: "", inviteType: "email", recipientMobile: "", recipientEmail: "" } });
                        setIsLoading(false);
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    })
                    .catch(error => {
                        setIsLoading(false);
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    });
            }
            else {
                ProviderService.sendInvite(modal.data)
                    .then(data => {
                        setModal({ visible: false, data: { recipientName: "", inviteType: "email", recipientMobile: "", recipientEmail: "" } });
                        setIsLoading(false);
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    })
                    .catch(error => {
                        setIsLoading(false);
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Provider MyClients sendInvite', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("MyClients")) {
                AuthService.markVisited("MyClients");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "MyClients"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    return (
        <div id="myClientsDiv">
            <ErrorBoundary errorAt="At Provider MyClients ProviderNavbar">
                <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            {
                userApprovalStatus ?
                    <div>
                        <ErrorBoundary errorAt="At Provider MyClients Joyride">
                            <Joyride
                                callback={handleJoyrideCallback}
                                continuous={true}
                                getHelpers={getHelpers}
                                run={tutorial.status}
                                scrollToFirstStep={true}
                                showProgress={true}
                                showSkipButton={true}
                                steps={tutorial.steps}
                                locale={{ last: 'Finish' }}
                                styles={{
                                    options: {
                                        primaryColor: "rgb(56, 78, 115)",
                                        zIndex: 10000,
                                    },
                                }}
                            />
                        </ErrorBoundary>
                        <div className="p-3">
                            <div className="d-flex">
                                <h1 className="my-auto" style={theme.headingBold}>My Clients</h1>
                                <Button id="customEmailBtn" className="btn ml-auto mr-3" style={theme.button} onClick={() => setCustomEmailModal({ ...customEmailModal, visible: true })}>Send Custom Email</Button>
                                <Button id="inviteBtn" className="btn mr-3" style={theme.button} onClick={() => setModal({ ...modal, visible: true })}>Send invite</Button>
                            </div>
                            <hr style={{ borderWidth: "2px" }} />

                            {profileComplete.status ? null : <Message message={profileComplete.message} />}

                            <p className="mb-3" style={theme.h3HeadingSemibold}><b>Total: {clientList.length}</b></p>

                            {clientList.map((client, index) => (
                                <ErrorBoundary errorAt={`At MyClients Clients${index}`}>
                                    <Clients index={index} client={client} openFormResponses={() => openFormResponses(index)} newClientAdded={notifications.newClientAdded} />
                                </ErrorBoundary>
                            ))}

                        </div>


                        {/* Custom Email Modal */}
                        <ErrorBoundary errorAt="At Provider MyClients CustomEmailModal">
                            <Modal size="lg" isOpen={customEmailModal.visible} backdrop={true}>
                                <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => { setCustomEmailModal({ visible: false }); setCustomEmailSubject(''); setCustomEmailBody(''); setselectedCustomRecipients([]); setSelectedCustomClients([]); setIsCopied(false); }}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Custom Email</span></ModalHeader>
                                <ModalBody>
                                    {/* <h4>Selected values: {selectedCustomRecipients}</h4> */}

                                    <Form onSubmit={sendCustomEmail}>

                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Select Clients to send Email</Label>
                                            <MultiSelect
                                                onChange={handleOnChangeCustomEmail}
                                                options={customEmailClientList}
                                                value={selectedCustomRecipients}
                                                labelledBy={"Select"}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>BCC String</Label>
                                            <div className="d-flex">

                                                <Input type="text" value={selectedCustomRecipients.map((item) => item.value).join(", ")} className="form-control" style={theme.formInput} disabled />
                                                <Button color="success" onClick={copyBCCString}>{isCopied ? "Copied!" : "Copy"}</Button>
                                            </div>
                                        </FormGroup>

                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Email Subject</Label>
                                            <Input type="text" value={customEmailSubject} onChange={handleCustomEmailSubject} className="form-control" style={theme.formInput} required />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Email Body</Label>
                                            <Input type="textarea" rows="10" value={customEmailBody} onChange={handleCustomEmailBody} className="form-control" style={theme.formInput} required />
                                        </FormGroup>

                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Email Preview</Label>
                                            <br />
                                            <Label style={theme.formBodyText} >Recipients: {selectedCustomClients.map((item) => item.email).join(", ")}</Label>
                                            <Input type="textarea" rows="12" className="form-control" style={theme.formInput} value={customEmailMsgPreview} disabled required />
                                        </FormGroup>

                                        <Button type="submit" style={theme.formButton} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Send</Button>
                                    </Form>

                                </ModalBody>
                            </Modal>
                        </ErrorBoundary>

                        {/* Invite Client Modal */}
                        <ErrorBoundary errorAt="At Provider MyClients Modal">
                            <Modal size="lg" isOpen={modal.visible} backdrop={true}>
                                <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ visible: false, data: { recipientName: "", inviteType: "email", recipientMobile: "", recipientEmail: "" } })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Invite Clients</span></ModalHeader>
                                <ModalBody>
                                    <Form onSubmit={sendInvite}>


                                        <FormGroup>
                                            <Label for="inviteType" style={theme.formBodyText} >Send via</Label>
                                            <Input type="select" name="inviteType" value={modal.data.inviteType} defaultValue='email' onChange={handleChange} className="form-control" style={theme.formInput} required >
                                                <option value='email'>Email</option>
                                                <option value='sms'>SMS</option>
                                                <option value='whatsapp'>WhatsApp</option>
                                            </Input>
                                        </FormGroup>
                                        {
                                            modal.data.inviteType === "email"
                                                ?
                                                // <FormGroup>
                                                //     <Label for="recipientEmail" style={theme.formBodyText} >Recipient Email</Label>
                                                //     <Input type="email" name="recipientEmail" value={modal.data.recipientEmail} onChange={handleChange} className="form-control" style={theme.formInput} required />
                                                // </FormGroup>
                                                emailRecipients.map((recipient, rid) => (
                                                    <div>

                                                        <div className="row align-items-end">
                                                            <div className="row col-8 col-sm-10 col-md-10">
                                                                <FormGroup className="col-sm-6 col-12 col-md-6">
                                                                    <Label style={theme.formBodyText}>Recipient Name #{rid + 1}</Label>
                                                                    <Input type="text" value={recipient.name} onChange={handleEmailRecipientsNameChange(rid)} className="form-control" style={theme.formInput} required />
                                                                </FormGroup>
                                                                <FormGroup className="col-sm-6 col-12 col-md-6">
                                                                    <Label style={theme.formBodyText}>Recipient Email #{rid + 1}</Label>
                                                                    <Input type="text" value={recipient.email} onChange={handleEmailRecipientsEmailChange(rid)} className="form-control" style={theme.formInput} required />
                                                                </FormGroup>
                                                            </div>

                                                            <div className="col-3 col-sm-2 col-md-2 mb-3">
                                                                <Button
                                                                    style={theme.formButtonDanger}
                                                                    onClick={handleRemoveEmailRecipient(rid)}>
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <hr />
                                                    </div>
                                                ))
                                                :
                                                modal.data.inviteType === "sms" || modal.data.inviteType === "whatsapp" ?
                                                    <div>
                                                        <FormGroup>
                                                            <Label for="recipientName" style={theme.formBodyText} >Recipient name</Label>
                                                            <Input type="text" name="recipientName" value={modal.data.recipientName} onChange={handleChange} className="form-control" style={theme.formInput} required />
                                                        </FormGroup>
                                                        <FormGroup>
                                                            <Label for="recipientMobile" style={theme.formBodyText} >Recipient Mobile</Label>
                                                            <Input type="mobile" name="recipientMobile" value={modal.data.recipientMobile} minLength="10" maxLength="10" onChange={handleChange} className="form-control" style={theme.formInput} required />
                                                        </FormGroup>
                                                    </div>
                                                    : null
                                        }
                                        {
                                            modal.data.inviteType === "email"
                                                ?
                                                <Button
                                                    style={theme.formButton}
                                                    onClick={handleAddEmailRecipient}
                                                >
                                                    Add Recipient
                                                </Button>
                                                : null
                                        }
                                        <FormGroup>
                                            <Label for="msgPreview" style={theme.formBodyText} >Message Preview<br />Will be sent to: {modal.data.inviteType === "email" ? emailRecipients ? Array.prototype.map.call(emailRecipients, function (recipient) { return recipient.email; }).join(", ") : '<Recipient Email>' : modal.data.recipientMobile ? `+91 ${modal.data.recipientMobile}` : 'Recipient Mobile'}</Label>
                                            <Input type="textarea" rows="12" name="msgPreview" className="form-control" style={theme.formInput} value={msgPreview} disabled required />
                                        </FormGroup>
                                        {modal.data.inviteType === "whatsapp" ? <a id="waLink" style={{ height: 0, visibility: 'hidden' }} href={waLink} target="_blank"></a> : null}
                                        <Button type="submit" style={theme.formButton} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Send</Button>
                                    </Form>
                                </ModalBody>
                            </Modal>
                        </ErrorBoundary>
                    </div>
                    :
                    <UserNotApproved />
            }
        </div>
    )

}

export default MyClients;